import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../apiClient';
import './HouseholdManager.css';

const HouseholdManager = () => {
  const { instance } = useMsal();
  const [households, setHouseholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Create household form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newHouseholdName, setNewHouseholdName] = useState('');

  // Add member form (per household)
  const [addMemberHouseholdId, setAddMemberHouseholdId] = useState(null);
  const [newMemberEmail, setNewMemberEmail] = useState('');

  const fetchHouseholds = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiFetchJson(instance, '/api/household-groups');
      const list = data?.$values ?? (Array.isArray(data) ? data : []);
      setHouseholds(list);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    fetchHouseholds();
  }, [fetchHouseholds]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleCreateHousehold = async (e) => {
    e.preventDefault();
    if (!newHouseholdName.trim()) return;

    try {
      setError(null);
      await apiFetchJson(instance, '/api/household-groups', {
        method: 'POST',
        body: JSON.stringify({ name: newHouseholdName.trim() }),
      });

      setNewHouseholdName('');
      setShowCreateForm(false);
      showSuccess(`Household "${newHouseholdName.trim()}" created!`);
      fetchHouseholds();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddMember = async (e, householdId) => {
    e.preventDefault();
    if (!newMemberEmail.trim()) return;

    try {
      setError(null);
      const data = await apiFetchJson(instance, `/api/household-groups/${householdId}/members`, {
        method: 'POST',
        body: JSON.stringify({ email: newMemberEmail.trim() }),
      });

      setNewMemberEmail('');
      setAddMemberHouseholdId(null);
      showSuccess(data?.message || 'Member added!');
      fetchHouseholds();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveMember = async (householdId, memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from this household?`)) return;

    try {
      setError(null);
      await apiFetchJson(instance, `/api/household-groups/${householdId}/members/${memberId}`, {
        method: 'DELETE',
      });

      showSuccess(`${memberName} removed from household.`);
      fetchHouseholds();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteHousehold = async (householdId, householdName) => {
    if (!window.confirm(`Delete household "${householdName}"? This will remove all members.`)) return;

    try {
      setError(null);
      await apiFetchJson(instance, `/api/household-groups/${householdId}`, {
        method: 'DELETE',
      });

      showSuccess(`Household "${householdName}" deleted.`);
      fetchHouseholds();
    } catch (err) {
      setError(err.message);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 1: return 'Owner';
      case 0: return 'Member';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return <div className="household-manager"><p>Loading households...</p></div>;
  }

  return (
    <div className="household-manager">
      <h2>👨‍👩‍👧‍👦 Household Groups</h2>
      <p className="subtitle">
        Household members share all property and finance data with each other.
      </p>

      {error && <div className="hh-error">{error}</div>}
      {successMessage && <div className="hh-success">{successMessage}</div>}

      {households.length === 0 ? (
        <div className="hh-empty">
          <p>You're not part of any household yet.</p>
          <p>Create one to start sharing data with family members.</p>
        </div>
      ) : (
        <div className="hh-list">
          {households.map((hh) => {
            const members = hh.members?.$values ?? hh.members ?? [];
            return (
              <div key={hh.id} className="hh-card">
                <div className="hh-card-header">
                  <h3>{hh.name}</h3>
                  <span className="hh-role-badge">{getRoleLabel(hh.myRole)}</span>
                </div>

                <div className="hh-members">
                  <h4>Members ({members.length})</h4>
                  <ul>
                    {members.map((m) => (
                      <li key={m.id} className="hh-member-row">
                        <div className="hh-member-info">
                          <span className="hh-member-name">{m.userName}</span>
                          <span className="hh-member-email">{m.userEmail}</span>
                          <span className={`hh-member-role ${m.role === 1 ? 'owner' : ''}`}>
                            {getRoleLabel(m.role)}
                          </span>
                        </div>
                        {hh.myRole === 1 && m.role !== 1 && (
                          <button
                            className="hh-btn-remove"
                            onClick={() => handleRemoveMember(hh.id, m.id, m.userName)}
                          >
                            ✕
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                {hh.myRole === 1 && (
                  <div className="hh-actions">
                    {addMemberHouseholdId === hh.id ? (
                      <form onSubmit={(e) => handleAddMember(e, hh.id)} className="hh-add-member-form">
                        <input
                          type="email"
                          placeholder="Enter email address"
                          value={newMemberEmail}
                          onChange={(e) => setNewMemberEmail(e.target.value)}
                          required
                          autoFocus
                        />
                        <button type="submit" className="hh-btn-primary">Add</button>
                        <button type="button" className="hh-btn-secondary" onClick={() => {
                          setAddMemberHouseholdId(null);
                          setNewMemberEmail('');
                        }}>Cancel</button>
                      </form>
                    ) : (
                      <div className="hh-button-row">
                        <button className="hh-btn-primary" onClick={() => setAddMemberHouseholdId(hh.id)}>
                          + Add Member
                        </button>
                        <button className="hh-btn-danger" onClick={() => handleDeleteHousehold(hh.id, hh.name)}>
                          Delete Household
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreateForm ? (
        <form onSubmit={handleCreateHousehold} className="hh-create-form">
          <input
            type="text"
            placeholder="Household name (e.g. 'Our Family')"
            value={newHouseholdName}
            onChange={(e) => setNewHouseholdName(e.target.value)}
            required
            autoFocus
          />
          <button type="submit" className="hh-btn-primary">Create</button>
          <button type="button" className="hh-btn-secondary" onClick={() => {
            setShowCreateForm(false);
            setNewHouseholdName('');
          }}>Cancel</button>
        </form>
      ) : (
        <button className="hh-btn-primary hh-create-btn" onClick={() => setShowCreateForm(true)}>
          + Create Household
        </button>
      )}
    </div>
  );
};

export default HouseholdManager;
